import useCheckPC from "@repo/hooks/utils/useCheckPC";
import classNames from "classnames";
import type { InputHTMLAttributes } from "react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

interface IOption {
  name: string;
  value: number | string;
}

interface IProps extends InputHTMLAttributes<HTMLSelectElement> {
  className?: string;
  options: IOption[];
  selected?: IOption;
  styleType?: "primary" | "secondary" | "text";
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
}

const PLACEHOLDER_VALUE = -1;

function Select({
  className,
  styleType = "primary",
  options,
  selected,
  placeholder,
  onChange,
  disabled,
  hasError,
  ...props
}: IProps) {
  const { isPC } = useCheckPC();

  const [hiddenWidth, setHiddenWidth] = useState<number>(72);
  const ref = useRef<HTMLSelectElement | null>(null);

  const [selectedOption, setSelectedOption] = useState(
    selected ?? { value: PLACEHOLDER_VALUE, name: placeholder ?? "" },
  );

  useEffect(() => {
    if (selected) {
      setSelectedOption(selected);
    }
  }, [selected]);

  useLayoutEffect(() => {
    if (ref.current) {
      setHiddenWidth(ref.current.clientWidth || 0);
    }
  }, [selectedOption]);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption({
      value: isNaN(Number(e.target.value)) ? e.target.value : parseInt(e.target.value),
      name: e.target.name,
    });
  };

  const selectedName = options.find((item) => item.value === selectedOption.value);

  return (
    <div
      className={classNames(
        className,
        'after:bg-sort relative rounded-md after:pointer-events-none after:absolute after:inline after:h-[14px] after:w-3.5 after:bg-contain after:content-[""]',
        styleType === "primary" && {
          "after:top-[7px]": isPC,
          "after:top-[10px]": !isPC,
          "after:right-3": true,
          "w-fit": true,
        },
        styleType === "secondary" && {
          "after:top-[10px]": true,
          "after:right-3": true,
          "w-full": true,
        },
        styleType === "text" && {
          "after:top-[3px]": true,
          "after:right-0": true,
          "w-fit": true,
          "h-5": true,
        },
        hasError && {
          border: true,
          "border-red-400": true,
        },
        disabled && {
          "opacity-50": true,
        },
      )}
    >
      <select
        className={classNames(
          "relative flex cursor-pointer appearance-none items-center gap-2.5 outline-hidden disabled:cursor-not-allowed",
          styleType === "primary" && {
            "min-w-30 rounded-md pr-9 pl-3": true,
            "h-7 py-1": isPC,
            "h-9 py-2": !isPC,
            "bg-btn-secondary": true,
            "text-btn-secondary": true,
          },
          styleType === "secondary" && {
            "min-w-30 rounded-md pr-9 pl-3": true,
            "font-regular": true,
            "w-full": true,
            "h-9 py-2": true,
            "bg-btn-tertiary": true,
            "text-btn-secondary": selectedOption.value !== PLACEHOLDER_VALUE,
            "text-btn-tertiary": selectedOption.value === PLACEHOLDER_VALUE,
          },
          styleType === "text" && {
            "bg-none text-right": true,
            "pr-4": true,
          },
        )}
        disabled={disabled}
        id={props.id}
        name={props.name}
        onChange={(e) => {
          handleSelect(e);
          onChange?.(e);
        }}
        style={{ width: `${styleType === "text" && hiddenWidth + 16}px` }}
        value={selectedOption.value}
        // defaultValue={selectedOption.value}
        {...props}
      >
        {Boolean(placeholder) && <option value={PLACEHOLDER_VALUE}>{placeholder}</option>}

        {options.map((option) => {
          return (
            <option id={option.name} key={option.name} value={option.value}>
              {option.name}
            </option>
          );
        })}
      </select>
      {styleType === "text" && (
        <select className="invisible" ref={ref}>
          <option>{selectedName?.name}</option>
        </select>
      )}
    </div>
  );
}

export default Select;
